import type { LucideIcon } from "lucide-react";
import {
  Banknote,
  BellRing,
  CalendarClock,
  CalendarDays,
  Coffee,
  ClipboardList,
  Eye,
  FileSignature,
  FolderOpen,
  Gauge,
  History,
  Home,
  IdCard,
  ListChecks,
  MessageSquare,
  LogOut,
  Mail,
  Settings2,
  Target,
  Timer,
  TrendingUp,
  UserRound,
  Users,
  Wallet,
} from "lucide-react";

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Shown in the mobile tab bar. At most five, or the bar gets crowded. */
  onTabBar?: boolean;
  /** Only for somebody holding a manager's position, or an administrator. */
  managerOnly?: boolean;
  /** Only for somebody who actually has people to review this month, which is
      not the same thing: HR can name anybody as an evaluator. */
  supervisorOnly?: boolean;
  /** Only for somebody who checks or files the salary sheets: Accounts, the
      WPS officer and the Accounting Manager. They are employees, not HR. */
  payrollChainOnly?: boolean;
  /** Only for a member of staff who also has HR's screens, an approver or
      somebody with HR access: the way between those and their own pages. */
  bothShellsOnly?: boolean;
  /**
   * Which figure from the counts endpoint is shown beside the item.
   *
   * A number beside "Leave" is how HR learns something is waiting without
   * opening the dashboard first, which is what they were doing on every
   * visit. Only queues carry one: a count of employees is not a to-do.
   */
  countKey?: string;
  /**
   * Where the item leads while its count is above zero: the same screen,
   * filtered to what the number counts. Without it a badge saying three
   * documents are waiting opens every document ever sent.
   */
  waitingTo?: string;
  /** A hint for the search box: other words somebody might type for it. */
  keywords?: string;
  /**
   * The letter that follows G to come here from the keyboard: G then E for
   * Employees. One lower-case letter, not used twice in the same list. The
   * same letter means the same kind of place for both roles, which is why
   * Home answers to D as the dashboard does, and why not every screen has
   * one: a letter nobody could guess is not worth learning.
   */
  goKey?: string;
}

export interface NavGroup {
  /** Groups keep the rail readable once Setup fills up. */
  heading?: string;
  items: NavItem[];
}

export const EMPLOYEE_NAV: NavGroup[] = [
  {
    items: [
      { to: "/home", label: "Home", icon: Home, onTabBar: true, goKey: "d" },
      { to: "/admin", label: "HR screens", icon: Eye, bothShellsOnly: true, keywords: "approve dashboard owner" },
    ],
  },
  {
    heading: "Your time",
    items: [
      { to: "/leaves", label: "Leave", icon: CalendarDays, onTabBar: true, keywords: "holiday annual sick request", goKey: "l" },
      { to: "/rota", label: "My rota", icon: CalendarClock, onTabBar: true, keywords: "shifts schedule week month", goKey: "r" },
      { to: "/breaks", label: "Breaks", icon: Coffee, goKey: "b" },
      { to: "/overtime", label: "Overtime", icon: Timer, keywords: "hours balance punches", goKey: "o" },
    ],
  },
  {
    heading: "Your work",
    items: [
      { to: "/documents", label: "Documents", icon: FileSignature, onTabBar: true, countKey: "documents", keywords: "sign letters papers certificate", goKey: "f" },
      { to: "/my-work", label: "My work", icon: ListChecks, countKey: "my_work", keywords: "tasks meetings", goKey: "t" },
      { to: "/kpi", label: "KPI reviews", icon: Target, supervisorOnly: true, countKey: "kpi", goKey: "k" },
      { to: "/chat", label: "Messages", icon: MessageSquare, onTabBar: true, keywords: "chat group", goKey: "m" },
      { to: "/reminders", label: "Reminders", icon: BellRing, managerOnly: true },
    ],
  },
  {
    // What a manager does for others: work given out, meetings called, the
    // breaks waiting on an answer, and where the branches will be short.
    // Hidden from everybody else, so the heading only appears for managers.
    heading: "Managing",
    items: [
      { to: "/work", label: "Tasks and meetings", icon: ClipboardList, managerOnly: true, keywords: "assign task meeting team" },
      // The number is breaks waiting at their branch, which is theirs to answer.
      { to: "/break-board", label: "Break board", icon: Coffee, managerOnly: true, countKey: "breaks", keywords: "breaks approve decline counter today" },
      { to: "/staffing-ahead", label: "Staffing ahead", icon: TrendingUp, managerOnly: true, keywords: "forecast short branches" },
    ],
  },
  {
    // Hidden from everybody but the people who check and file the pay, so
    // the heading only appears for them.
    heading: "Pay",
    items: [
      { to: "/payroll", label: "Salary sheets", icon: Wallet, payrollChainOnly: true, keywords: "payroll wps month approve" },
      { to: "/pay-changes", label: "Pay changes", icon: History, payrollChainOnly: true, keywords: "salary raise reason history" },
    ],
  },
  {
    // Resigning is a real thing a person needs to be able to find, but it is
    // not a daily errand, and a permanent "Leaving" in the main list reads
    // like a suggestion. Down here it is findable without being an invitation.
    heading: "Your account",
    items: [
      { to: "/profile", label: "My profile", icon: UserRound, keywords: "pay salary details", goKey: "p" },
      { to: "/my-kpi", label: "My performance", icon: Gauge, keywords: "kpi score" },
      { to: "/leaving", label: "Leaving", icon: LogOut, keywords: "resign notice" },
    ],
  },
];

/**
 * Grouped, because fourteen items in one column is a list to be read rather
 * than a rail to be aimed at. The headings name the question somebody arrived
 * with — who works here, when are they working, what are they doing, what has
 * been signed — so the eye lands in the right third before it reads a word.
 */
export const ADMIN_NAV: NavGroup[] = [
  {
    items: [
      { to: "/admin", label: "Dashboard", icon: Gauge, onTabBar: true, goKey: "d" },
      { to: "/home", label: "My own pages", icon: Home, bothShellsOnly: true, keywords: "my leave profile documents sign" },
    ],
  },
  {
    heading: "People",
    items: [
      { to: "/admin/employees", label: "Employees", icon: Users, onTabBar: true, countKey: "employees", waitingTo: "/admin/employees?account_status=pending", keywords: "staff people review approve", goKey: "e" },
      { to: "/admin/papers", label: "Papers expiring", icon: IdCard, countKey: "papers", waitingTo: "/admin/papers?state=expired", keywords: "visa passport emirates id labour card insurance contract expiry expired probation" },
      { to: "/admin/change-requests", label: "Profile changes", icon: ClipboardList, countKey: "change_requests", goKey: "c" },
      { to: "/admin/leaving", label: "Joining and leaving", icon: LogOut, countKey: "leaving", keywords: "resignation offboarding onboarding", goKey: "j" },
    ],
  },
  {
    heading: "Time",
    items: [
      { to: "/admin/leaves", label: "Leave", icon: CalendarDays, onTabBar: true, countKey: "leaves", keywords: "holiday requests approve", goKey: "l" },
      { to: "/admin/rota", label: "Rota", icon: CalendarClock, onTabBar: true, keywords: "shifts schedule draw week", goKey: "r" },
      { to: "/admin/breaks", label: "Breaks", icon: Coffee, countKey: "breaks", goKey: "b" },
      { to: "/admin/forecast", label: "Staffing ahead", icon: TrendingUp, keywords: "forecast hiring" },
      { to: "/admin/overtime", label: "Overtime", icon: Timer, countKey: "overtime", keywords: "attendance punches badges import", goKey: "o" },
      { to: "/admin/kpi", label: "KPI reviews", icon: Target, countKey: "kpi", keywords: "performance month", goKey: "k" },
    ],
  },
  {
    heading: "Work",
    items: [
      { to: "/admin/work", label: "Tasks and meetings", icon: ListChecks, keywords: "assign reminders", goKey: "t" },
      { to: "/admin/chat", label: "Messages", icon: MessageSquare, keywords: "chat group", goKey: "m" },
      { to: "/admin/letters", label: "Letters", icon: Mail, countKey: "letters", keywords: "warning certificate noc" },
      { to: "/admin/salary", label: "Salary reviews", icon: Banknote, countKey: "salary_reviews", keywords: "raise increment" },
      { to: "/admin/payroll", label: "Salary sheets", icon: Wallet, keywords: "payroll wps month" },
      { to: "/admin/pay-changes", label: "Pay changes", icon: History, keywords: "salary raise reason history" },
    ],
  },
  {
    heading: "Documents",
    items: [
      { to: "/admin/documents", label: "For signature", icon: FileSignature, countKey: "documents", waitingTo: "/admin/documents?status=waiting", keywords: "sign send", goKey: "f" },
      { to: "/admin/library", label: "Company documents", icon: FolderOpen, keywords: "library policies files folders" },
    ],
  },
  {
    items: [{ to: "/admin/settings", label: "Settings", icon: Settings2, keywords: "branches positions departments staffing holidays groups stamp kpi reviewers", goKey: "s" }],
  },
];

/** The item an address belongs to, whatever follows its "?". */
/** Where an item leads: its filtered queue while something is waiting, else the screen. */
export function linkFor(item: NavItem, waiting: number): string {
  return waiting > 0 && item.waitingTo ? item.waitingTo : item.to;
}

export function navItemAt(nav: readonly NavGroup[], to: string): NavItem | undefined {
  const path = to.split("?")[0];
  for (const group of nav) {
    const found = group.items.find((item) => item.to === path);
    if (found) return found;
  }
  return undefined;
}
